import { Decimal } from './money.js';
import { contourGeometry, parseMeasure, roomRoof, roomWalls, } from './measurements.js';
/**
 * Раскладка стен и проёмов для схемы помещения: 2D-плана, развёртки стен и
 * 3D-вида. Раскладка общая, чтобы окно на плане и на развёртке стояло в
 * одном и том же месте.
 *
 * Прямоугольное помещение раскладывается как контур из четырёх стен — дальше
 * у схем один путь для любой формы.
 *
 * Координаты здесь — number: это геометрия для рисования, в деньги она не
 * идёт. Площади стен для подписей считаются отдельно через Decimal
 * (wallAreas), чтобы их сумма сходилась с «Площадью стен» в смете.
 */
/** Высота подоконника, если прораб её не указал: типовая для жилых помещений. */
export const DEFAULT_SILL_HEIGHT = '0.8';
/** Больше копий одного проёма на схеме не рисуем — это явная ошибка ввода. */
const MAX_COPIES = 50;
/**
 * Стена проёма. Проёмы без стены — из смет, заполненных до появления схем:
 * окна ставим на первую стену, двери на третью (у прямоугольника — верхняя и
 * нижняя), чтобы план был хотя бы правдоподобен; прораб перенесёт их, выбрав стену.
 */
export function openingWall(opening, wallCount = 4) {
    return opening.wall ?? (opening.kind === 'WINDOW' ? 1 : Math.min(3, wallCount));
}
function num(value) {
    return parseMeasure(value)?.toNumber() ?? 0;
}
export function layoutRoom(room) {
    const contour = roomWalls(room);
    if (!contour)
        return null;
    const geometry = contourGeometry(contour);
    const height = num(room.height);
    const roof = roomRoof(room);
    const walls = contour.map((wall, i) => {
        const [x1, y1] = geometry.vertices[i];
        const [x2, y2] = geometry.vertices[i + 1] ?? geometry.end;
        const length = num(wall.length);
        const profile = roof
            ? roof.walls[i].points.map(([t, h]) => [t.toNumber(), h.toNumber()])
            : [
                [0, height],
                [length, height],
            ];
        return {
            number: i + 1,
            x1: x1.toNumber(),
            y1: y1.toNumber(),
            x2: x2.toNumber(),
            y2: y2.toNumber(),
            length,
            direction: wall.direction,
            profile,
        };
    });
    const ceiling = roof
        ? roof.regions.map((region) => ({
            polygon: region.polygon.map(([x, y]) => [x.toNumber(), y.toNumber()]),
            heights: region.polygon.map(([x, y]) => Math.max(region.plane.c.plus(region.plane.a.times(x)).plus(region.plane.b.times(y)).toNumber(), 0)),
            sloped: region.plane.wall !== null,
        }))
        : [
            {
                polygon: geometry.vertices.map(([x, y]) => [x.toNumber(), y.toNumber()]),
                heights: geometry.vertices.map(() => height),
                sloped: false,
            },
        ];
    const placed = [];
    // Проёмы без точной позиции расставляются равномерно по своей стене.
    const auto = new Map();
    room.openings.forEach((opening, sourceIndex) => {
        const w = num(opening.width);
        const h = num(opening.height);
        if (w <= 0 || h <= 0)
            return;
        const count = Math.min(Math.floor(num(opening.count)), MAX_COPIES);
        let wall = openingWall(opening, walls.length);
        // Стену удалили из контура, а проём остался на ней — показываем на
        // последней стене как ошибку, а не теряем молча.
        const missingWall = wall < 1 || wall > walls.length;
        if (missingWall)
            wall = walls.length;
        const bottom = opening.kind === 'WINDOW' ? num(opening.sillHeight ?? DEFAULT_SILL_HEIGHT) : 0;
        // Отступ однозначен только для одиночного проёма; несколько одинаковых
        // окон одной строкой — это «три окна на стене», их ставим равномерно.
        const offset = count === 1 ? parseMeasure(opening.offset) : null;
        for (let i = 0; i < count; i++) {
            const item = {
                kind: opening.kind,
                wall,
                sourceIndex,
                start: offset?.toNumber() ?? 0,
                width: w,
                bottom,
                height: h,
                conflict: missingWall,
            };
            placed.push(item);
            if (!offset)
                auto.set(wall, [...(auto.get(wall) ?? []), item]);
        }
    });
    for (const [wall, items] of auto) {
        const wallLen = walls[wall - 1].length;
        const total = items.reduce((acc, o) => acc + o.width, 0);
        const gap = Math.max((wallLen - total) / (items.length + 1), 0);
        let cursor = gap;
        for (const item of items) {
            item.start = cursor;
            cursor += item.width + gap;
        }
    }
    markConflicts(placed, walls);
    return {
        walls,
        height,
        ceiling,
        openings: placed,
        clockwise: !geometry.signedArea.isNegative(),
        gap: geometry.gap.toNumber(),
        closed: geometry.closed,
    };
}
// Допуск на погрешность float при сравнении краёв: проём вплотную к углу — не ошибка.
const EPS = 1e-6;
/** Высота стены на расстоянии t от левого угла — по линии потолка. */
export function wallHeightAt(wall, t) {
    const points = wall.profile;
    if (points.length === 0)
        return 0;
    if (t <= points[0][0])
        return points[0][1];
    for (let i = 1; i < points.length; i++) {
        const [t1, h1] = points[i];
        if (t <= t1) {
            const [t0, h0] = points[i - 1];
            return t1 === t0 ? h1 : h0 + ((h1 - h0) * (t - t0)) / (t1 - t0);
        }
    }
    return points.at(-1)[1];
}
function markConflicts(openings, walls) {
    for (const o of openings) {
        const wall = walls[o.wall - 1];
        // Линия потолка выпуклая вверх (нижняя огибающая прямых), поэтому над
        // проёмом она ниже всего у одного из его краёв — проверять края достаточно.
        const top = o.bottom + o.height;
        const ceilingOk = (t) => {
            const h = wallHeightAt(wall, t);
            return h <= 0 || top <= h + EPS;
        };
        const tooTall = !ceilingOk(o.start) || !ceilingOk(o.start + o.width);
        if (o.start + o.width > wall.length + EPS || tooTall)
            o.conflict = true;
    }
    for (let i = 0; i < openings.length; i++) {
        for (let j = i + 1; j < openings.length; j++) {
            const a = openings[i];
            const b = openings[j];
            if (a.wall !== b.wall)
                continue;
            const overlapX = a.start < b.start + b.width - EPS && b.start < a.start + a.width - EPS;
            const overlapY = a.bottom < b.bottom + b.height - EPS && b.bottom < a.bottom + a.height - EPS;
            if (overlapX && overlapY) {
                a.conflict = true;
                b.conflict = true;
            }
        }
    }
}
/**
 * Площадь каждой стены за вычетом её проёмов — для подписей на развёртке.
 * Считается через Decimal из исходных строк, как calcRoom, поэтому сумма по
 * стенам равна WALL_AREA помещения (когда проёмы не больше стен).
 * Индекс массива — номер стены минус один.
 */
export function wallAreas(room) {
    const contour = roomWalls(room);
    if (!contour)
        return null;
    const height = parseMeasure(room.height) ?? new Decimal(0);
    const openingsByWall = contour.map(() => new Decimal(0));
    for (const opening of room.openings) {
        const area = (parseMeasure(opening.width) ?? new Decimal(0))
            .times(parseMeasure(opening.height) ?? new Decimal(0))
            .times(parseMeasure(opening.count) ?? new Decimal(0));
        const wall = Math.min(Math.max(openingWall(opening, contour.length), 1), contour.length);
        openingsByWall[wall - 1] = openingsByWall[wall - 1].plus(area);
    }
    // У мансарды стена — под линией потолка, как и в calcRoom.
    const roof = roomRoof(room);
    return contour.map((wall, i) => {
        const gross = roof?.walls[i]?.area ?? (parseMeasure(wall.length) ?? new Decimal(0)).times(height);
        return Decimal.max(gross.minus(openingsByWall[i]), 0);
    });
}
//# sourceMappingURL=room-layout.js.map